import { useState, useEffect } from 'react';
import { FormInstance } from 'antd';
import { SignatureData } from '@/features/laudo/types/laudo-wizard.types';
import { evaluatorSignatureService } from '@/services/evaluatorSignature.service';

interface UseSignatureDataProps {
  data: SignatureData;
  onUpdate: (data: Partial<SignatureData>) => void;
  dataPreloaded: boolean;
  form: FormInstance;
}

export function useSignatureData({
  data,
  onUpdate,
  dataPreloaded,
  form,
}: UseSignatureDataProps) {
  const [loading, setLoading] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [originalData, setOriginalData] = useState<SignatureData>({
    evaluatorName: '',
    creaCauNumber: '',
    signatureImage: '',
  });

  useEffect(() => {
    if (dataPreloaded) {
      initializePreloadedData();
    } else {
      loadSavedData();
    }
  }, [dataPreloaded]);

  const initializePreloadedData = () => {
    if (data.evaluatorName || data.signatureImage) {
      form.setFieldsValue({
        evaluatorName: data.evaluatorName,
        creaCauNumber: data.creaCauNumber,
      });
      const preloadedData = { ...data, _saved: true };
      setOriginalData(preloadedData);
      // Atualizar o estado global com a flag _saved
      onUpdate({ _saved: true });
      if (data.signatureImage) {
        setSignatureUrl(data.signatureImage);
      }
    }
  };

  const loadSavedData = async () => {
    try {
      setLoading(true);
      const savedData = await evaluatorSignatureService.getSignature();

      if (savedData) {
        const loadedData = {
          evaluatorName: savedData.evaluatorName,
          creaCauNumber: savedData.creaCauNumber || '',
          signatureImage: savedData.signatureBase64,
          _saved: true, // Marca como salvo pois foi carregado do servidor
        };

        form.setFieldsValue({
          evaluatorName: savedData.evaluatorName,
          creaCauNumber: savedData.creaCauNumber,
        });

        onUpdate(loadedData);
        setOriginalData(loadedData);

        if (savedData.signatureBase64) {
          setSignatureUrl(savedData.signatureBase64);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const checkIfDirty = (newData: Partial<SignatureData>) => {
    const currentData = { ...data, ...newData };
    return (
      currentData.evaluatorName !== originalData.evaluatorName ||
      currentData.creaCauNumber !== originalData.creaCauNumber ||
      currentData.signatureImage !== originalData.signatureImage
    );
  };

  const updateSignatureImage = (base64: string) => {
    const newData = { signatureImage: base64, _saved: false };
    onUpdate(newData);
    setSignatureUrl(base64);
    setIsDirty(checkIfDirty(newData));
  };

  const removeSignatureImage = () => {
    const newData = { signatureImage: '', _saved: false };
    onUpdate(newData);
    setSignatureUrl(null);
    setIsDirty(checkIfDirty(newData));
  };

  const updateFormData = (values: any) => {
    const newData = {
      evaluatorName: values.evaluatorName || '',
      creaCauNumber: values.creaCauNumber || '',
      _saved: false, // Marca como não salvo quando alterar os dados
    };
    onUpdate(newData);
    setIsDirty(checkIfDirty(newData));
  };

  const markAsSaved = (savedData: SignatureData) => {
    setOriginalData(savedData);
    setIsDirty(false);
  };

  return {
    loading,
    setLoading,
    signatureUrl,
    isDirty,
    originalData,
    updateSignatureImage,
    removeSignatureImage,
    updateFormData,
    markAsSaved,
  };
}
